import Checkbox from '@allesauto/core/checkbox';
import DropdownRange from '@allesauto/core/dropdownRange';
import SearchDropdown, { type Option } from '@allesauto/core/searchDropdown';
import Slider from '@allesauto/core/slider';
import TextLink from '@allesauto/core/textLink';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { consts } from '@allesauto/utils/consts';
import { DropdownOpenHeight } from '@allesauto/utils/consts';
import { generateRangeOfNumbers, getCurrentYear } from '@allesauto/utils/funcs';
import { getSlug } from '@allesauto/utils/funcs';
import debounce from 'lodash/debounce';
import { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { VehicleConditionStateProps } from '@/store/redux/reducers/pages/pcp/filters/vehicleCondition';

export interface VehicleConditionFilterProps {
  checkboxesItems: SchemaTypes.Condition[];
  registrationMinYear: number;
  registrationMaxYear: number;
  previousOwnersMaxValue: number;
  selected: VehicleConditionStateProps;
  onChange: (value: VehicleConditionStateProps) => void;
}

const VehicleConditionFilter: FC<
  React.PropsWithChildren<VehicleConditionFilterProps>
> = ({
  checkboxesItems,
  registrationMinYear = 1975,
  registrationMaxYear = getCurrentYear(),
  previousOwnersMaxValue,
  onChange,
  selected,
}: VehicleConditionFilterProps) => {
  const [showAll, setShowAll] = useState<boolean>(false);

  const [maxPrevOwnerSliderValue, setMaxPrevOwnerSliderValue] =
    useState<number>(0);
  const [prevOwnerSliderHasPlus, setPrevOwnerSliderHasPlus] =
    useState<boolean>(false);

  const {
    PCP: {
      FILTERS: {
        VEHICLE_CONDITION: {
          MILEAGE_DROP_DOWN_ITEMS,
          NR_OF_CHECKBOXES_SHOWN,
          PREVIOUS_OWNERS_SLIDER_VALUES,
        },
      },
    },
  } = consts;

  const intl = useIntl();

  const trackingIdentifierCondition = 'aa-filter--condition';
  const trackingIdentifierMileage = 'aa-filter--mileage';
  const trackingIdentifierRegistration = 'aa-filter--registration';
  const trackingIdentifierPreowner = 'aa-filter--preowner';

  useEffect(() => {
    if (previousOwnersMaxValue) {
      if (previousOwnersMaxValue > PREVIOUS_OWNERS_SLIDER_VALUES.max) {
        setMaxPrevOwnerSliderValue(PREVIOUS_OWNERS_SLIDER_VALUES.max);
        setPrevOwnerSliderHasPlus(true);
      } else {
        setMaxPrevOwnerSliderValue(previousOwnersMaxValue);
      }
    } else {
      setMaxPrevOwnerSliderValue(PREVIOUS_OWNERS_SLIDER_VALUES.max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckboxChange = useCallback(
    (key: string) => {
      const sel = [...(selected.condition || [])];
      const keyIndex = sel.indexOf(key);
      if (keyIndex > -1) {
        sel.splice(keyIndex, 1);
      } else {
        sel.push(key);
      }
      onChange({ ...selected, condition: sel });
    },
    [selected, onChange]
  );

  const handleChangeMileageDropdown = useCallback(
    (value: Option) => {
      onChange({ ...selected, mileage: value?.value as number });
    },
    [selected, onChange]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeRange = useCallback(
    (value: any) => {
      onChange({
        ...selected,
        minYear: value.minValue?.value,
        maxYear: value.maxValue?.value,
      });
    },
    [selected, onChange]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const maxOwnersSliderChange = useCallback(
    debounce((v) => onChange({ ...selected, maxOwners: v }), 400),
    [
      selected?.minYear,
      selected?.maxYear,
      selected?.condition,
      selected?.mileage,
      selected?.maxOwners,
      onChange,
    ]
  );

  const checkboxesToShow = showAll
    ? checkboxesItems
    : checkboxesItems && checkboxesItems.slice(0, NR_OF_CHECKBOXES_SHOWN);

  const className = {
    label: 'font-regular font-weight-normal text-p1 pt-0 pb-10px text-white',
  };

  return (
    <div>
      <p className={className.label}>
        {translate(intl, 'ui.PCP.FILTERS.VEHICLE_CONDITION.FILTER_LABEL')}
      </p>
      <div className="mb-40px" data-tracking-id={trackingIdentifierCondition}>
        {checkboxesToShow &&
          checkboxesToShow.map(({ name, displayName }) => (
            <div key={name} className="mt-20px">
              <Checkbox
                id={`${trackingIdentifierCondition}-${getSlug(name)}`}
                key={name}
                darkBg
                label={displayName}
                onChange={() => {
                  handleCheckboxChange(name);
                }}
                checked={
                  selected.condition && selected.condition.includes(name)
                }
              />
            </div>
          ))}
      </div>
      {checkboxesItems && checkboxesItems.length > NR_OF_CHECKBOXES_SHOWN && (
        <TextLink
          customClassName="mb-40px"
          text={translate(
            intl,
            showAll
              ? 'ui.PCP.FILTERS.VEHICLE_CONDITION.VIEW_LESS_BTN_LABEL'
              : 'ui.PCP.FILTERS.VEHICLE_CONDITION.VIEW_ALL_BTN_LABEL'
          )}
          onClick={() => {
            setShowAll(!showAll);
          }}
          isDarkBg={true}
        />
      )}
      <p className="font-weight-normal pb-10px pt-0 text-p1 text-white">
        {translate(
          intl,
          'ui.PCP.FILTERS.VEHICLE_CONDITION.MILEAGE_DROP_DOWN_LABEL'
        )}
      </p>
      <div className="pb-40px" data-tracking-id={trackingIdentifierMileage}>
        <SearchDropdown
          id={trackingIdentifierMileage}
          context="relative-position"
          options={MILEAGE_DROP_DOWN_ITEMS}
          darkBg={true}
          hasLabelAnimation={false}
          placeholder={translate(
            intl,
            'ui.PCP.FILTERS.VEHICLE_CONDITION.MILEAGE_DROP_DOWN_DEFAULT_VALUE'
          )}
          isSearchable={false}
          openHeight={DropdownOpenHeight.MEDIUM}
          value={
            MILEAGE_DROP_DOWN_ITEMS.find(
              (item) => item.value === selected.mileage
            ) || null
          }
          onChange={(value: Option | readonly Option[] | null) =>
            handleChangeMileageDropdown(value as Option)
          }
        />
      </div>
      <p className="font-weight-normal pb-10px pt-0 text-p1 text-white">
        {translate(
          intl,
          'ui.PCP.FILTERS.VEHICLE_CONDITION.FIRST_REGISTRATION_LABEL'
        )}
      </p>
      <div
        className="pb-40px"
        data-tracking-id={trackingIdentifierRegistration}
      >
        <DropdownRange
          id={trackingIdentifierRegistration}
          rangeValues={{
            minValue: selected.minYear
              ? {
                  value: selected.minYear as number,
                  label: selected.minYear as number,
                }
              : null,
            maxValue: selected.maxYear
              ? {
                  value: selected.maxYear as number,
                  label: selected.maxYear as number,
                }
              : null,
          }}
          darkBg
          context="relative-position"
          minOptions={generateRangeOfNumbers(
            registrationMinYear,
            registrationMaxYear
          )}
          maxOptions={generateRangeOfNumbers(
            registrationMinYear,
            registrationMaxYear
          )}
          value={null}
          minLabel={translate(
            intl,
            'ui.PCP.FILTERS.VEHICLE_CONDITION.FIRST_REGISTRATION_DROP_DOWN_STARTING_YEAR_DEFAULT_VALUE'
          )}
          maxLabel={translate(
            intl,
            'ui.PCP.FILTERS.VEHICLE_CONDITION.FIRST_REGISTRATION_DROP_DOWN_ENDING_YEAR_DEFAULT_VALUE'
          )}
          openHeight={DropdownOpenHeight.MEDIUM}
          onChangeRange={handleOnChangeRange}
        />
      </div>
      <p className={className.label}>
        {translate(
          intl,
          'ui.PCP.FILTERS.VEHICLE_CONDITION.PREVIOUS_VEHICLE_OWNERS_LABEL'
        )}
      </p>
      <div
        className="-mt-20px w-[98%]"
        data-tracking-id={trackingIdentifierPreowner}
      >
        <Slider
          id={trackingIdentifierPreowner}
          darkBg={true}
          rangeProps={{}}
          min={PREVIOUS_OWNERS_SLIDER_VALUES.min || 0}
          max={maxPrevOwnerSliderValue}
          step={PREVIOUS_OWNERS_SLIDER_VALUES.step || 1}
          rangeLabelText={translate(
            intl,
            'ui.PCP.FILTERS.VEHICLE_CONDITION.PREVIOUS_VEHICLE_OWNERS_SLIDER_STEP_LABEL'
          )}
          useFloatValues={false}
          value={[selected.maxOwners ?? maxPrevOwnerSliderValue]}
          tooltipFormatter={(value) => {
            return value !== null &&
              !Array.isArray(value) &&
              value === maxPrevOwnerSliderValue &&
              prevOwnerSliderHasPlus
              ? `${value}+`
              : `${value}`;
          }}
          onChange={(value) => {
            maxOwnersSliderChange(value);
          }}
        />
      </div>
    </div>
  );
};

export default VehicleConditionFilter;
